'use client'

import type { ContentPage, ComponentNode } from '@school-journey/shared-types'
import { SafeHtml } from '@/lib/sanitize/safe-html'

interface ContentPageWebRendererProps {
  /** The full ContentPage block-array object, or null when unset. */
  content: ContentPage | null | undefined
  className?: string
}

/**
 * Shared web renderer for a ContentPage block array (the shape produced by the
 * Puck "Custom Pages" builder and the event Overview editor). Walks
 * `content.content` in order and dispatches by block `type`.
 *
 * MVP renders only `RichText` blocks (via `SafeHtml`, wrapped in `prose`);
 * every other block type renders nothing (fail-safe forward-compat as the
 * palette grows). Null/empty content renders nothing.
 */
export function ContentPageWebRenderer({ content, className }: ContentPageWebRendererProps) {
  const blocks = content?.content
  if (!blocks || blocks.length === 0) return null

  return (
    <div className={className}>
      {blocks.map((node, index) => (
        <BlockRenderer key={index} node={node} />
      ))}
    </div>
  )
}

function BlockRenderer({ node }: { node: ComponentNode }) {
  switch (node.type) {
    case 'RichText':
      return <SafeHtml className="prose prose-sm max-w-none dark:prose-invert" html={node.props.body} />
    default:
      // Unknown / not-yet-supported block types render nothing (fail-safe).
      return null
  }
}
